/**
 * Curated font pairings for the Fias plugin builder.
 *
 * Each pairing names the heading/body/mono CSS font-family strings plus
 * a `families` array of references into the `@fias/platform-resources`
 * catalog. The host page constructs a `PlatformResourcesClient` and
 * calls `getFontCssUrls(pairing.families.map((f) => f.family))` to
 * resolve CDN URLs. The SDK itself does not compose URLs.
 *
 * Breaking change vs. the previous `googleFontsUrl` field: external
 * plugins consuming `FontPairing` must update to read `families`. See
 * the SDK CHANGELOG for the major-version migration.
 */

export interface FontFamilyRef {
  /** CSS `font-family` name — matches an entry in the
   *  `@fias/platform-resources` catalog. */
  family: string;
}

export interface FontPairing {
  id: string;
  name: string;
  body: string;
  heading: string;
  mono: string;
  /** Catalog references the host page resolves through a
   *  `PlatformResourcesClient`. Empty for the `system` pairing (uses OS
   *  fonts only — no vendored bytes). */
  families: FontFamilyRef[];
}

/**
 * Build `families` array literals tersely.
 */
function fams(...names: string[]): FontFamilyRef[] {
  return names.map((family) => ({ family }));
}

export const FONT_PAIRINGS: FontPairing[] = [
  {
    id: 'system',
    name: 'System Default',
    heading: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    body: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
    families: [],
  },
  {
    id: 'inter-jakarta',
    name: 'Modern Sans',
    heading: "'Plus Jakarta Sans', sans-serif",
    body: "'Inter', sans-serif",
    mono: "'JetBrains Mono', monospace",
    families: fams('Inter', 'Plus Jakarta Sans', 'JetBrains Mono'),
  },
  {
    id: 'playfair-lato',
    name: 'Editorial Serif',
    heading: "'Playfair Display', serif",
    body: "'Lato', sans-serif",
    mono: "'Source Code Pro', monospace",
    families: fams('Playfair Display', 'Lato', 'Source Code Pro'),
  },
  {
    id: 'space-grotesk',
    name: 'Tech Forward',
    heading: "'Space Grotesk', sans-serif",
    body: "'Space Grotesk', sans-serif",
    mono: "'Space Mono', monospace",
    families: fams('Space Grotesk', 'Space Mono'),
  },
  {
    id: 'poppins-nunito',
    name: 'Friendly Rounded',
    heading: "'Poppins', sans-serif",
    body: "'Nunito', sans-serif",
    mono: "'Fira Code', monospace",
    families: fams('Poppins', 'Nunito', 'Fira Code'),
  },
  {
    id: 'dm-sans-serif',
    name: 'Clean Geometric',
    heading: "'DM Sans', sans-serif",
    body: "'DM Sans', sans-serif",
    mono: "'DM Mono', monospace",
    families: fams('DM Sans', 'DM Mono'),
  },
  {
    id: 'outfit-work',
    name: 'Professional',
    heading: "'Outfit', sans-serif",
    body: "'Work Sans', sans-serif",
    mono: "'IBM Plex Mono', monospace",
    families: fams('Outfit', 'Work Sans', 'IBM Plex Mono'),
  },
  {
    id: 'crimson-raleway',
    name: 'Classic Elegant',
    heading: "'Crimson Pro', serif",
    body: "'Raleway', sans-serif",
    mono: "'Inconsolata', monospace",
    families: fams('Crimson Pro', 'Raleway', 'Inconsolata'),
  },
  {
    id: 'rubik-karla',
    name: 'Soft Modern',
    heading: "'Rubik', sans-serif",
    body: "'Karla', sans-serif",
    mono: "'Ubuntu Mono', monospace",
    families: fams('Rubik', 'Karla', 'Ubuntu Mono'),
  },
  {
    id: 'montserrat-open',
    name: 'Bold Impact',
    heading: "'Montserrat', sans-serif",
    body: "'Open Sans', sans-serif",
    mono: "'Roboto Mono', monospace",
    families: fams('Montserrat', 'Open Sans', 'Roboto Mono'),
  },
  {
    id: 'lexend-atkinson',
    name: 'Accessible',
    heading: "'Lexend', sans-serif",
    body: "'Atkinson Hyperlegible', sans-serif",
    mono: "'JetBrains Mono', monospace",
    families: fams('Lexend', 'Atkinson Hyperlegible', 'JetBrains Mono'),
  },
  {
    id: 'sora-manrope',
    name: 'Startup',
    heading: "'Sora', sans-serif",
    body: "'Manrope', sans-serif",
    mono: "'Fira Code', monospace",
    families: fams('Sora', 'Manrope', 'Fira Code'),
  },
  {
    id: 'caveat-quicksand',
    name: 'Handwritten',
    heading: "'Caveat', cursive",
    body: "'Quicksand', sans-serif",
    mono: "'Fira Code', monospace",
    families: fams('Caveat', 'Quicksand', 'Fira Code'),
  },
  {
    id: 'bebas-source',
    name: 'Bold Display',
    heading: "'Bebas Neue', sans-serif",
    body: "'Source Sans 3', sans-serif",
    mono: "'Source Code Pro', monospace",
    families: fams('Bebas Neue', 'Source Sans 3', 'Source Code Pro'),
  },
  {
    id: 'righteous-nunito',
    name: 'Retro Fun',
    heading: "'Righteous', sans-serif",
    body: "'Nunito Sans', sans-serif",
    mono: "'Ubuntu Mono', monospace",
    families: fams('Righteous', 'Nunito Sans', 'Ubuntu Mono'),
  },
  {
    id: 'dancing-merriweather',
    name: 'Elegant Script',
    heading: "'Dancing Script', cursive",
    body: "'Merriweather', serif",
    mono: "'Inconsolata', monospace",
    families: fams('Dancing Script', 'Merriweather', 'Inconsolata'),
  },
  {
    id: 'pacifico-lato',
    name: 'Playful',
    heading: "'Pacifico', cursive",
    body: "'Lato', sans-serif",
    mono: "'Roboto Mono', monospace",
    families: fams('Pacifico', 'Lato', 'Roboto Mono'),
  },
  {
    id: 'abril-assistant',
    name: 'Magazine',
    heading: "'Abril Fatface', serif",
    body: "'Assistant', sans-serif",
    mono: "'JetBrains Mono', monospace",
    families: fams('Abril Fatface', 'Assistant', 'JetBrains Mono'),
  },
  {
    id: 'comfortaa-cabin',
    name: 'Soft Rounded',
    heading: "'Comfortaa', sans-serif",
    body: "'Cabin', sans-serif",
    mono: "'DM Mono', monospace",
    families: fams('Comfortaa', 'Cabin', 'DM Mono'),
  },
  {
    id: 'cinzel-cormorant',
    name: 'Luxury',
    heading: "'Cinzel', serif",
    body: "'Cormorant Garamond', serif",
    mono: "'Inconsolata', monospace",
    families: fams('Cinzel', 'Cormorant Garamond', 'Inconsolata'),
  },
];

/** Look up a font pairing by ID. Returns the system default if not found. */
export function getFontPairingById(id: string): FontPairing {
  return FONT_PAIRINGS.find((f) => f.id === id) ?? FONT_PAIRINGS[0];
}
